import { PayloadAction, createAsyncThunk, createSlice, nanoid } from '@reduxjs/toolkit'

import { apiZodios } from '~api/apiZodios'
import {
  BillStep1,
  BillStepId,
  CalculatedTag,
  CaseStatus,
  ChargeSubStepCreate,
  ChargeSubstep,
  CopySectionSubStepViewRequestData,
  CreateFormStyleElementViewRequestData,
  CreateInputStepViewRequestData,
  CreateSectionStepViewRequestData,
  DatetimeStep,
  DeleteFormStyleElementViewRequestData,
  DeleteInputStepViewRequestData,
  DeleteSectionSubstepViewRequestData,
  DeleteStepAssignmentViewRequestData,
  EditBillStepViewRequestData,
  EditDateTimeStepViewRequestData,
  EditFormStepViewRequestData,
  EditFormStyleElementRequestData,
  EditIdentityStepViewRequestData,
  EditInputStepViewRequestData,
  EditSectionSubStepViewRequestData,
  EditStepViewRequestData,
  Expression,
  ExpressionId,
  FormNodeTypeEnum,
  FormStep0,
  FormStepId,
  FormStyleElement,
  FormStyleElementId,
  FormStyleElementType,
  IdentityStep,
  InputStepChoice1,
  InputStepId,
  InputStepSub0,
  InputStyleEnum,
  InputSubstep2,
  Process3,
  ProcessId,
  SectionStepId,
  SectionSubstep0,
  StepCreate,
  StepId,
  StepNotification,
  StepType,
  Value0,
  ValueId,
  ValueType,
  ValueTypeEnum,
} from '~types'

export type SelectedStep = {
  stepId: FormStepId | BillStepId
  openEditor: boolean
  type: StepType
}

export type SelectedSubstep = {
  substepId: InputStepId | SectionStepId | FormStyleElementId
  type: FormNodeTypeEnum | 'page_break'
}

export enum ModalType {
  publish = 'publish',
  share = 'share',
  expression = 'expression',
  assignees = 'assignees',
  billStep = 'billStep',
  tags = 'tags',
}

export enum DatabaseSaveStatus {
  idle = 'idle',
  loading = 'loading',
  succeeded = 'succeeded',
  failed = 'failed',
}

export interface SnackbarConfiguration {
  show: boolean
  message: string
  severity: 'success' | 'error' | 'info' | 'warning'
}

interface ProcessEditState {
  currentProcess: Process3 | null
  selectedStep: SelectedStep | null
  selectedFormSubstep: SelectedSubstep | null
  status: DatabaseSaveStatus
  error: string | null
  userId: string | null // UserId
  expressions: Record<ExpressionId, Expression>
  modal: ModalType | null
  stepNotifications: Record<StepId, StepNotification[]>
  snackbarConfiguration: SnackbarConfiguration
}

const initialState: ProcessEditState = {
  currentProcess: null,
  selectedStep: null,
  selectedFormSubstep: null,
  status: DatabaseSaveStatus.idle,
  error: null,
  userId: null,
  expressions: {},
  modal: null,
  stepNotifications: {},
  snackbarConfiguration: {
    show: false,
    message: '',
    severity: 'success',
  },
}

export const createInputStep = createAsyncThunk(
  'processEdit/createInputStep',
  async (
    params: Omit<CreateInputStepViewRequestData, 'user_id'> & { temporaryId: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const { input_step: inputStep } = await apiZodios.cases_createInputStep({
        user_id: userId,
        value_type: params.value_type as ValueType,
        name: params.name as string,
        description: params.description as string,
        template: params.template as ValueId | null,
        style: params.style as InputStyleEnum,
        required: params.required as boolean,
        section: (params.section ?? null) as SectionStepId | null,
        form: params.form as FormStepId,
        pinned: params.pinned as boolean,
        order: params.order as number,
        process_id: params.process_id as string,
      })

      return {
        inputStepId: inputStep.id,
        datetimeStepSub: inputStep?.sub?.datetime_step_sub,
        identityStepSub: inputStep?.sub?.identity_step_sub,
        order: params.order,
        temporaryId: params.temporaryId,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createBillStep = createAsyncThunk(
  'processEdit/createBillStep',
  async (params: StepCreate & { temporary_id: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const response = await apiZodios.cases_createBillStep({
        user_id: userId,
        step_create: {
          process_id: params.process_id as string,
          order: params.order as number,
        },
      })

      return {
        billStepId: response.bill_step_id,
        stepId: response.step_id,
        order: params.order,
        temporaryId: params.temporary_id,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createFormStep = createAsyncThunk(
  'processEdit/createFormStep',
  async (params: StepCreate & { temporary_id: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const response = await apiZodios.cases_createFormStep({
        user_id: userId,
        step_create: {
          process_id: params.process_id as string,
          order: params.order as number,
        },
      })

      return {
        formStepId: response.form_step_id,
        stepId: response.step_id,
        order: params.order,
        temporaryId: params.temporary_id,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createFormStyleElement = createAsyncThunk(
  'processEdit/createFormStyleElement',
  async (
    params: Omit<CreateFormStyleElementViewRequestData, 'user_id'> & { temporary_id: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const { form_style_element: formStyleElement } = await apiZodios.cases_createFormStyleElement(
        {
          user_id: userId,
          form_step_id: params.form_step_id as string,
          order: params.order as number,
          type: params.type as FormStyleElementType,
        },
      )
      return {
        temporaryId: params.temporary_id,
        formStyleElementId: formStyleElement.id,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createSectionStep = createAsyncThunk(
  'processEdit/createSectionStep',
  async (
    params: Omit<CreateSectionStepViewRequestData, 'user_id'> & { temporaryId: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const response = await apiZodios.cases_createSectionStep({
        user_id: userId,
        name: params.name as string,
        description: params.description as string,
        section: params.section as SectionStepId | null,
        form: params.form as FormStepId,
        order: params.order as number,
        button_name: params.button_name as string,
        min: params.min as number,
        max: params.max as number,
        render: params.render as ExpressionId | null,
      })

      return {
        sectionStepId: response.section_step_id,
        order: params.order,
        temporaryId: params.temporaryId,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const copySectionStep = createAsyncThunk(
  'processEdit/copySectionStep',
  async (
    params: Omit<CopySectionSubStepViewRequestData, 'user_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const {
        section_step_id: sectionStepId,
        copied_sections: copiedSections,
        copied_inputs: copiedInputs,
      } = await apiZodios.cases_copySectionSubStep({
        user_id: userId,
        section_step_id: params.section_step_id as SectionStepId,
        section_id: params.section_id as SectionStepId | null,
        form_step_id: params.form_step_id as FormStepId,
        order: params.order as number,
      })
      return {
        sectionStepId: sectionStepId,
        copiedSections: copiedSections,
        copiedInputs: copiedInputs,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteBillStep = createAsyncThunk(
  'processEdit/deleteBillStep',
  async (params: { bill_step_id: BillStepId }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      await apiZodios.cases_deleteBillStep({
        user_id: userId,
        bill_step_id: params.bill_step_id,
      })

      return {}
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteFormStep = createAsyncThunk(
  'processEdit/deleteFormStep',
  async (params: { form_step_id: FormStepId }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      await apiZodios.cases_deleteFormStep({
        user_id: userId,
        form_step_id: params.form_step_id,
      })

      return {}
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteFormStyleElement = createAsyncThunk(
  'processEdit/deleteFormStyleElement',
  async (
    params: Omit<DeleteFormStyleElementViewRequestData, 'user_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      await apiZodios.cases_deleteFormStyleElement({
        user_id: userId,
        form_style_element_id: params.form_style_element_id as FormStyleElementId,
      })

      return {
        formStyleElementId: params.form_style_element_id,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteInputStep = createAsyncThunk(
  'processEdit/deleteInputStep',
  async (
    params: Omit<DeleteInputStepViewRequestData, 'user_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const response = await apiZodios.cases_deleteInputSubstep({
        user_id: userId,
        input_step_id: params.input_step_id as string,
      })

      return {
        stepId: params.input_step_id,
        response,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteSectionStep = createAsyncThunk(
  'processEdit/deleteSectionStep',
  async (
    params: Omit<DeleteSectionSubstepViewRequestData, 'user_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const response = await apiZodios.cases_deleteSectionSubstep({
        user_id: userId,
        section_substep_id: params.section_substep_id as string,
      })

      return {
        stepId: params.section_substep_id,
        response,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editDatetimeStep = createAsyncThunk(
  'processEdit/editDatetimeStep',
  async (
    params: Omit<EditDateTimeStepViewRequestData, 'user_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const response = await apiZodios.cases_editDateTimeStep({
        user_id: userId,
        datetime_step_id: params.datetime_step_id as string,
        request_time: params.request_time as boolean,
      })

      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editIdentityStep = createAsyncThunk(
  'processEdit/editIdentityStep',
  async (
    params: Omit<EditIdentityStepViewRequestData, 'user_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const response = await apiZodios.cases_editIdentityStep({
        user_id: userId,
        identity_step_id: params.identity_step_id as string,
        ...params,
      })
      return {
        response,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editInputStep = createAsyncThunk(
  'processEdit/editInputStep',
  async (params: Omit<EditInputStepViewRequestData, 'user_id'>, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const response = await apiZodios.cases_editInputStep({
        user_id: userId,
        input_step_id: params.input_step_id as string,
        ...params,
      })

      return {
        stepId: params.input_step_id,
        updates: params,
        response,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editSectionStep = createAsyncThunk(
  'processEdit/editSectionStep',
  async (
    params: Omit<EditSectionSubStepViewRequestData, 'user_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const response = await apiZodios.cases_editSectionSubStep({
        user_id: userId,
        section_step_id: params.section_sub_step_id as string,
        ...params,
      })

      return {
        stepId: params.section_step_id,
        updates: params,
        response,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editBillStep = createAsyncThunk(
  'processEdit/editBillStep',
  async (params: Omit<EditBillStepViewRequestData, 'user_id'>, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }
      const { bill_step: updatedBillStep } = await apiZodios.cases_editBillStep({
        user_id: userId,
        bill_step_id: params.bill_step_id as string,
        name: params.name as string,
        description: params.description as string,
        order: params.order as number,
        charges: params.charges as ChargeSubStepCreate[],
        ...params,
      })

      return {
        stepId: params.bill_step_id,
        updates: params,
        charges: updatedBillStep.charges,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editFormStep = createAsyncThunk(
  'processEdit/editFormStep',
  async (params: Omit<EditFormStepViewRequestData, 'user_id'>, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const response = await apiZodios.cases_editFormStep({
        user_id: userId,
        form_step_id: params.form_step_id as string,
        ...params,
      })

      return {
        stepId: params.form_step_id,
        updates: params,
        response,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editFormStyleElement = createAsyncThunk(
  'processEdit/editFormStyleElement',
  async (
    params: Omit<EditFormStyleElementRequestData, 'user_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      await apiZodios.cases_editFormStyleElement({
        user_id: userId,
        form_style_element_id: params.form_style_element_id as string,
        ...params,
      })

      return {
        formStyleElementId: params.form_style_element_id,
        updates: params,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editStep = createAsyncThunk(
  'processEdit/editStep',
  async (params: Omit<EditStepViewRequestData, 'user_id'>, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const response = await apiZodios.cases_editStep({
        user_id: userId,
        step_id: params.step_id as string,
        set_status: params.set_status as CaseStatus | null,
        calculated_tags: params.calculated_tags as CalculatedTag[],
      })

      return {
        stepId: params.step_id,
        updates: { set_status: params.set_status },
        response,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteStepAssignment = createAsyncThunk(
  'processEdit/deleteStepAssignment',
  async (
    params: Omit<DeleteStepAssignmentViewRequestData, 'user_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      const response = await apiZodios.cases_deleteStepAssignment({
        user_id: userId,
        step_id: params.step_id as string,
        user_expression_id: params.user_expression_id as ExpressionId,
      })

      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

const processEditSlice = createSlice({
  name: 'processEdit',
  initialState,
  reducers: {
    setCurrentProcess: (state, action: PayloadAction<{ process: Process3 }>) => {
      state.currentProcess = action.payload.process
    },
    setSelectedStep: (state, action: PayloadAction<{ selectedStep: SelectedStep | null }>) => {
      state.selectedStep = action.payload.selectedStep
    },
    setSelectedFormSubstep: (
      state,
      action: PayloadAction<{ selectedSubstep: SelectedSubstep | null }>,
    ) => {
      state.selectedFormSubstep = action.payload.selectedSubstep
    },
    setUserId: (state, action: PayloadAction<{ userId: string | null }>) => {
      state.userId = action.payload.userId
    },
    addExpressionsLocally: (state, action: PayloadAction<{ expressions: Expression[] }>) => {
      const { expressions } = action.payload
      const newExpressions = {
        ...state.expressions,
        ...expressions.reduce<Record<string, Expression>>((acc, expression) => {
          acc[expression.id] = expression
          return acc
        }, {}),
      }
      state.expressions = newExpressions
    },
    deleteExpressionsLocally: (state, action: PayloadAction<{ expressionIds: ExpressionId[] }>) => {
      const { expressionIds } = action.payload
      const newExpressions = { ...state.expressions }
      expressionIds.forEach((id) => {
        delete newExpressions[id]
      })
      state.expressions = newExpressions
    },
    setExpressions: (
      state,
      action: PayloadAction<{ expressions: Record<ExpressionId, Expression> }>,
    ) => {
      state.expressions = action.payload.expressions
    },
    setModal: (state, action: PayloadAction<{ modal: ModalType | null }>) => {
      state.modal = action.payload.modal
    },
    setStepNotifications: (
      state,
      action: PayloadAction<{ stepNotifications: Record<StepId, StepNotification[]> }>,
    ) => {
      state.stepNotifications = action.payload.stepNotifications
    },
    setSnackbarConfiguration: (state, action: PayloadAction<SnackbarConfiguration>) => {
      state.snackbarConfiguration = action.payload
    },
    clearProcessEdit: (state) => {
      state.currentProcess = null
      state.selectedStep = null
      state.selectedFormSubstep = null
    },
    createStepLocally: (
      state,
      action: PayloadAction<
        StepCreate & {
          type: StepType
          temporaryId: string
        }
      >,
    ) => {
      if (action.payload.type === StepType.bill) {
        if (state.currentProcess) {
          state.currentProcess = {
            ...state.currentProcess,
            bill_steps: [
              ...state.currentProcess.bill_steps,
              {
                charges: [],
                id: action.payload.temporaryId,
                process: action.payload.process_id as ProcessId,
                code: '', // not needed
                step: nanoid(),
                name: '',
                description: '',
                installments: 1,
                order: action.payload.order,
                account: null,
                distribution: null,
                set_status: null,
                added_at: null,
                removed_at: null,
                allow_ach: true,
                calculated_tags: [],
              } as BillStep1,
            ],
          }
        }
      } else if (action.payload.type === StepType.form) {
        if (state.currentProcess) {
          state.currentProcess = {
            ...state.currentProcess,
            form_steps: [
              ...state.currentProcess.form_steps,
              {
                id: action.payload.temporaryId,
                description: '',
                calculated_tags: [],
                process: action.payload.process_id,
                code: '', // not needed
                step: nanoid(),
                name: '',
                order: action.payload.order,
                external: true,
                due: null,
                sla: null,
                button_name: null,
                set_status: null,
                added_at: null,
                removed_at: null,
              } as FormStep0,
            ],
          }
        }
      }
    },
    createInputStepLocally: (
      state,
      action: PayloadAction<
        Omit<CreateInputStepViewRequestData, 'user_id'> & { temporaryId: string }
      >,
    ) => {
      if (state.currentProcess) {
        let substep = { type: action.payload.type }
        if (action.payload.type === ValueTypeEnum.enum.identity) {
          substep = {
            type: action.payload.type,
            identity_step_sub: {
              id: action.payload.temporaryId,
              input_step: action.payload.temporaryId,
              type: ValueTypeEnum.enum.identity,
              require_me: true,
              show_address: true,
              require_address: false,
              show_date_of_birth: false,
              require_date_of_birth: false,
              show_email: true,
              require_email: true,
              verify_email: false,
              show_name: true,
              require_name: false,
              show_phone: true,
              require_phone: false,
              add_contact: false,
            },
          } as InputStepSub0
        } else if (action.payload.type === ValueTypeEnum.enum.datetime) {
          substep = {
            type: action.payload.type,
            datetime_step_sub: {
              id: action.payload.temporaryId,
              input_step: action.payload.temporaryId,
              type: ValueTypeEnum.enum.datetime,
              request_time: false,
            },
          } as InputStepSub0
        }
        state.currentProcess = {
          ...state.currentProcess,
          inputs: [
            ...state.currentProcess.inputs,
            {
              id: action.payload.temporaryId,
              sub: substep as InputStepSub0,
              choices: [],
              template: action.payload.template as ValueId | null,
              form: action.payload.form as FormStepId,
              type: action.payload.value_type as ValueType,
              process: state.currentProcess.id,
              code: '',
              name: action.payload.name,
              description: action.payload.description,
              order: action.payload.order,
              style: action.payload.style,
              section: action.payload.section,
              required: action.payload.required,
              external: false,
              sla: null,
              due: null,
              pinned: action.payload.pinned,
              added_at: null,
              removed_at: null,
            } as InputSubstep2,
          ],
        }
      }
    },
    createSectionStepLocally: (
      state,
      action: PayloadAction<
        Omit<CreateSectionStepViewRequestData, 'user_id'> & { temporaryId: string }
      >,
    ) => {
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          subsections: [
            ...state.currentProcess.subsections,
            {
              id: action.payload.temporaryId,
              process: state.currentProcess.id as ProcessId,
              name: action.payload.name,
              description: action.payload.description,
              order: action.payload.order,
              form: action.payload.form as FormStepId,
              section: action.payload.section as SectionStepId | null,
              button_name: action.payload.button_name,
              added_at: null,
              removed_at: null,
              multiple: (action.payload.max as number) > 1,
              min: action.payload.min,
              max: action.payload.max,
              render: action.payload.render as ExpressionId | null,
            } as SectionSubstep0,
          ],
        }
      }
    },
    createFormStyleElementLocally: (
      state,
      action: PayloadAction<
        Omit<CreateFormStyleElementViewRequestData, 'user_id'> & { temporary_id: string }
      >,
    ) => {
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          page_breaks: [
            ...state.currentProcess.page_breaks,
            {
              id: action.payload.temporary_id,
              form_step: action.payload.form_step_id,
              type: action.payload.type,
              order: action.payload.order,
            } as FormStyleElement,
          ],
        }
      }
    },
    copySectionStepLocally: (
      state,
      action: PayloadAction<{
        orderedSections: Array<
          Omit<CreateSectionStepViewRequestData, 'user_id'> & {
            previous_id: SectionStepId
          }
        >
        orderedInputs: Array<
          Omit<CreateInputStepViewRequestData, 'user_id'> & {
            previous_id: InputStepId
          }
        >
      }>,
    ) => {
      if (state.currentProcess) {
        const copiedInputs = action.payload.orderedInputs.map((newInput) => {
          const newTempId = `${newInput.previous_id}-copy`
          let substep = { type: newInput.type }
          if (newInput.type === ValueTypeEnum.enum.identity) {
            substep = {
              type: newInput.type,
              identity_step_sub: {
                id: newTempId,
                input_step: newTempId,
                type: ValueTypeEnum.enum.identity,
                require_me: true,
                show_address: true,
                require_address: false,
                show_date_of_birth: false,
                require_date_of_birth: false,
                show_email: true,
                require_email: true,
                verify_email: false,
                show_name: true,
                require_name: false,
                show_phone: true,
                require_phone: false,
                add_contact: false,
              },
            } as InputStepSub0
          } else if (newInput.type === ValueTypeEnum.enum.datetime) {
            substep = {
              type: newInput.type,
              datetime_step_sub: {
                id: newTempId,
                input_step: newTempId,
                type: ValueTypeEnum.enum.datetime,
                request_time: false,
              },
            } as InputStepSub0
          }
          return {
            id: newTempId,
            sub: substep as InputStepSub0,
            choices: [],
            template: newInput.template as ValueId | null,
            form: newInput.form as FormStepId,
            type: newInput.value_type as ValueType,
            process: state.currentProcess?.id,
            code: '',
            name: newInput.name,
            description: newInput.description,
            order: newInput.order,
            style: newInput.style,
            section: newInput.section,
            required: newInput.required,
            external: false,
            sla: null,
            due: null,
            pinned: newInput.pinned,
            added_at: null,
            removed_at: null,
          } as InputSubstep2
        })
        const copiedSections = action.payload.orderedSections.map((newSection) => {
          return {
            id: `${newSection.previous_id}-copy`,
            process: state.currentProcess?.id as ProcessId,
            name: newSection.name,
            description: newSection.description,
            order: newSection.order,
            form: newSection.form as FormStepId,
            section: newSection.section as SectionStepId | null,
            button_name: newSection.button_name,
            added_at: null,
            removed_at: null,
            multiple: (newSection.max as number) > 1,
            min: newSection.min,
            max: newSection.max,
            render: newSection.render,
          } as SectionSubstep0
        })
        state.currentProcess = {
          ...state.currentProcess,
          inputs: [...state.currentProcess.inputs, ...copiedInputs],
          subsections: [...state.currentProcess.subsections, ...copiedSections],
        }
      }
    },
    deleteInputStepLocally: (
      state,
      action: PayloadAction<Omit<DeleteInputStepViewRequestData, 'user_id'>>,
    ) => {
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.filter(
            (input) => input.id !== action.payload.input_step_id,
          ),
        }
      }
    },
    deleteSectionStepLocally: (
      state,
      action: PayloadAction<Omit<DeleteSectionSubstepViewRequestData, 'user_id'>>,
    ) => {
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          subsections: state.currentProcess.subsections.filter(
            (subsection) => subsection.id !== action.payload.section_substep_id,
          ),
        }
      }
    },
    deleteFormStyleElementLocally: (
      state,
      action: PayloadAction<Omit<DeleteFormStyleElementViewRequestData, 'user_id'>>,
    ) => {
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          page_breaks: state.currentProcess.page_breaks.filter(
            (pageBreak) => pageBreak.id !== action.payload.form_style_element_id,
          ),
        }
      }
    },
    deleteStepLocally: (
      state,
      action: PayloadAction<{
        stepId: FormStepId | BillStepId
        type: StepType
      }>,
    ) => {
      if (action.payload.type === StepType.bill && state.currentProcess && action.payload.stepId) {
        state.currentProcess = {
          ...state.currentProcess,
          bill_steps: state.currentProcess.bill_steps.filter(
            (billStep) => billStep.id !== action.payload.stepId,
          ),
        }
      } else if (
        action.payload.type === StepType.form &&
        state.currentProcess &&
        action.payload.stepId
      ) {
        state.currentProcess = {
          ...state.currentProcess,
          form_steps: state.currentProcess.form_steps.filter(
            (formStep) => formStep.id !== action.payload.stepId,
          ),
        }
      }
    },
    deleteStepAssignmentLocally: (
      state,
      action: PayloadAction<Omit<DeleteStepAssignmentViewRequestData, 'user_id'>>,
    ) => {
      const { step_id: stepId, user_expression_id: userExpressionId } = action.payload
      const updatedStepNotifications = { ...state.stepNotifications }
      if (updatedStepNotifications[stepId as StepId]) {
        updatedStepNotifications[stepId as StepId] = updatedStepNotifications[
          stepId as StepId
        ].filter((notification) => notification.user !== userExpressionId)
      }
      state.stepNotifications = updatedStepNotifications
    },
    updateBillStepLocally: (
      state,
      action: PayloadAction<Omit<EditBillStepViewRequestData, 'user_id'>>,
    ) => {
      const { bill_step_id: billStepId, ...updates } = action.payload
      const chargeSubstepCreates = updates.charges as ChargeSubStepCreate[] | undefined

      if (state.currentProcess && state.currentProcess.bill_steps) {
        state.currentProcess = {
          ...state.currentProcess,
          bill_steps: state.currentProcess.bill_steps.map((billStep) => {
            if (billStep.id === billStepId) {
              let newCharges: ChargeSubstep[]
              if (chargeSubstepCreates) {
                newCharges = chargeSubstepCreates.map((charge) => ({
                  id: nanoid(),
                  bill: billStepId,
                  dollars: charge.dollars,
                  code: '',
                  name: charge.name,
                  description: charge.description,
                  dollars_type: ValueTypeEnum.enum.number,
                }))
              } else {
                newCharges = billStep.charges
              }
              return {
                ...billStep,
                name: updates.name ?? billStep.name,
                description: updates.description ?? billStep.description,
                order: updates.order ?? billStep.order,
                charges: newCharges,
                account: updates.account ?? billStep.account,
                distribution: updates.distribution ?? billStep.distribution,
              } as BillStep1
            }
            return billStep
          }),
        }
      }
    },
    updateFormStepLocally: (
      state,
      action: PayloadAction<Omit<EditFormStepViewRequestData, 'user_id'>>,
    ) => {
      const { form_step_id: formStepId, ...updates } = action.payload

      // Update the step in the current process if it exists
      if (state.currentProcess && state.currentProcess.form_steps) {
        state.currentProcess = {
          ...state.currentProcess,
          form_steps: state.currentProcess.form_steps.map((formStep) => {
            if (formStep.id === formStepId) {
              return {
                ...formStep,
                // Map API field names to your local model field names if needed
                name: updates.name ?? formStep.name,
                description: updates.description ?? formStep.description,
                order: updates.order ?? formStep.order,
                external: updates.external ?? formStep.external,
                sla: updates.sla ?? formStep.sla,
                due: updates.due ?? formStep.due,
                button_name:
                  updates.button_name === undefined ? formStep.button_name : updates.button_name,
              } as FormStep0
            }
            return formStep
          }),
        }
      }
    },
    updateStepLocally: (state, action: PayloadAction<Omit<EditStepViewRequestData, 'user_id'>>) => {
      const {
        step_id: stepId,
        set_status: setStatus,
        calculated_tags: calculatedTags,
      } = action.payload
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          form_steps: state.currentProcess.form_steps.map((formStep) => {
            if (formStep.step === stepId) {
              return {
                ...formStep,
                set_status: setStatus,
                calculated_tags: calculatedTags,
              } as FormStep0
            }
            return formStep
          }),
          bill_steps: state.currentProcess.bill_steps.map((billStep) => {
            if (billStep.step === stepId) {
              return {
                ...billStep,
                set_status: setStatus,
                calculated_tags: calculatedTags,
              } as BillStep1
            }
            return billStep
          }),
        }
      }
    },
    updateInputStepLocally: (
      state,
      action: PayloadAction<
        Omit<EditInputStepViewRequestData, 'user_id' | 'choices' | 'template'> & {
          choices?: InputStepChoice1[]
          template?: Value0 | null
        }
      >,
    ) => {
      const { input_step_id: inputStepId, ...updates } = action.payload
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.map((inputStep) => {
            if (inputStep.id === inputStepId) {
              return {
                ...inputStep,
                name: updates.name ?? inputStep.name,
                description: updates.description ?? inputStep.description,
                required: updates.required ?? inputStep.required,
                pinned: updates.pinned ?? inputStep.pinned,
                style: updates.style ?? inputStep.style,
                choices: updates.choices ?? inputStep.choices,
                order: updates.order ?? inputStep.order,
                section: updates.section === undefined ? inputStep.section : updates.section,
                template: updates.template === undefined ? inputStep.template : updates.template,
              } as InputSubstep2
            }
            return inputStep
          }),
        }
      }
    },
    updateDateTimeStepLocally: (
      state,
      action: PayloadAction<Omit<EditDateTimeStepViewRequestData, 'user_id'>>,
    ) => {
      if (state.currentProcess) {
        const { datetime_step_id: dateTimeStepId, ...updates } = action.payload
        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.map((inputStep) => {
            if (
              inputStep.sub.datetime_step_sub &&
              inputStep.sub.datetime_step_sub.id === dateTimeStepId
            ) {
              return {
                ...inputStep,
                sub: {
                  ...inputStep.sub,
                  datetime_step_sub: {
                    ...inputStep.sub.datetime_step_sub,
                    request_time:
                      updates.request_time ?? inputStep.sub.datetime_step_sub.request_time,
                  } as DatetimeStep,
                } as InputStepSub0,
              }
            }
            return inputStep
          }),
        }
      }
    },
    updateSectionStepLocally: (
      state,
      action: PayloadAction<Omit<EditSectionSubStepViewRequestData, 'user_id'>>,
    ) => {
      const { section_step_id: sectionStepId, ...updates } = action.payload
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          subsections: state.currentProcess.subsections.map((sectionStep) => {
            if (sectionStep.id === sectionStepId) {
              return {
                ...sectionStep,
                name: updates.name ?? sectionStep.name,
                description: updates.description ?? sectionStep.description,
                order: updates.order ?? sectionStep.order,
                button_name:
                  updates.button_name === undefined ? sectionStep.button_name : updates.button_name,
                min: updates.min ?? sectionStep.min,
                max: updates.max ?? sectionStep.max,
                render: updates.render === undefined ? sectionStep.render : updates.render,
                section: updates.section === undefined ? sectionStep.section : updates.section,
              } as SectionSubstep0
            }
            return sectionStep
          }),
        }
      }
    },
    updateFormStyleElementLocally: (
      state,
      action: PayloadAction<Omit<EditFormStyleElementRequestData, 'user_id'>>,
    ) => {
      const { form_style_element_id: formStyleElementId, ...updates } = action.payload

      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          page_breaks: state.currentProcess.page_breaks.map((formStyleElement) => {
            if (formStyleElement.id === formStyleElementId) {
              return {
                ...formStyleElement,
                order: updates.order ?? formStyleElement.order,
              } as FormStyleElement
            }
            return formStyleElement
          }),
        }
      }
    },
    updateIdentityStepLocally: (
      state,
      action: PayloadAction<Omit<EditIdentityStepViewRequestData, 'user_id'>>,
    ) => {
      if (state.currentProcess) {
        const { identity_step_id: identityStepId, ...updates } = action.payload
        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.map((inputStep) => {
            if (
              inputStep.sub.identity_step_sub &&
              inputStep.sub.identity_step_sub.id === identityStepId
            ) {
              return {
                ...inputStep,
                sub: {
                  ...inputStep.sub,
                  identity_step_sub: {
                    ...inputStep.sub.identity_step_sub,
                    add_contact: updates.add_contact ?? inputStep.sub.identity_step_sub.add_contact,
                    require_me: updates.require_me ?? inputStep.sub.identity_step_sub.require_me,
                    show_address:
                      updates.show_address ?? inputStep.sub.identity_step_sub.show_address,
                    require_address:
                      updates.require_address ?? inputStep.sub.identity_step_sub.require_address,
                    show_date_of_birth:
                      updates.show_date_of_birth ??
                      inputStep.sub.identity_step_sub.show_date_of_birth,
                    require_date_of_birth:
                      updates.require_date_of_birth ??
                      inputStep.sub.identity_step_sub.require_date_of_birth,
                    show_email: updates.show_email ?? inputStep.sub.identity_step_sub.show_email,
                    require_email:
                      updates.require_email ?? inputStep.sub.identity_step_sub.require_email,
                    verify_email:
                      updates.verify_email ?? inputStep.sub.identity_step_sub.verify_email,
                    show_name: updates.show_name ?? inputStep.sub.identity_step_sub.show_name,
                    require_name:
                      updates.require_name ?? inputStep.sub.identity_step_sub.require_name,
                    show_phone: updates.show_phone ?? inputStep.sub.identity_step_sub.show_phone,
                    require_phone:
                      updates.require_phone ?? inputStep.sub.identity_step_sub.require_phone,
                  } as IdentityStep,
                } as InputStepSub0,
              }
            }
            return inputStep
          }),
        }
      }
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state: ProcessEditState): void => {
      state.status = DatabaseSaveStatus.loading
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleRejected = (state: ProcessEditState, action: PayloadAction<any>): void => {
      state.status = DatabaseSaveStatus.failed
      state.error = (action.payload as string) || 'Failed to update step'
    }

    const handleBasicFulfilled = (state: ProcessEditState): void => {
      state.status = DatabaseSaveStatus.succeeded
    }

    const pendingActions = [
      deleteBillStep.pending,
      deleteFormStep.pending,
      deleteInputStep.pending,
      deleteSectionStep.pending,
      deleteFormStyleElement.pending,
      deleteStepAssignment.pending,
      editBillStep.pending,
      editDatetimeStep.pending,
      editFormStep.pending,
      editFormStyleElement.pending,
      editIdentityStep.pending,
      editInputStep.pending,
      editSectionStep.pending,
      editStep.pending,
      createBillStep.pending,
      createFormStep.pending,
      createInputStep.pending,
      createSectionStep.pending,
      createFormStyleElement.pending,
      copySectionStep.pending,
    ] as const
    const basicFulfilledActions = [
      deleteBillStep.fulfilled,
      deleteFormStep.fulfilled,
      deleteInputStep.fulfilled,
      deleteFormStyleElement.fulfilled,
      deleteSectionStep.fulfilled,
      deleteStepAssignment.fulfilled,
      editDatetimeStep.fulfilled,
      editFormStep.fulfilled,
      editFormStyleElement.fulfilled,
      editIdentityStep.fulfilled,
      editInputStep.fulfilled,
      editSectionStep.fulfilled,
      editStep.fulfilled,
    ] as const
    const rejectedActions = [
      deleteBillStep.rejected,
      deleteFormStep.rejected,
      deleteInputStep.rejected,
      deleteFormStyleElement.rejected,
      deleteSectionStep.rejected,
      deleteStepAssignment.rejected,
      editBillStep.rejected,
      editDatetimeStep.rejected,
      editFormStep.rejected,
      editFormStyleElement.rejected,
      editIdentityStep.rejected,
      editInputStep.rejected,
      editSectionStep.rejected,
      editStep.rejected,
      createBillStep.rejected,
      createFormStep.rejected,
      createInputStep.rejected,
      createSectionStep.rejected,
      createFormStyleElement.rejected,
      copySectionStep.rejected,
    ] as const

    pendingActions.forEach((actionType) => {
      builder.addCase(actionType, handlePending)
    })

    basicFulfilledActions.forEach((actionType) => {
      builder.addCase(actionType, handleBasicFulfilled)
    })

    rejectedActions.forEach((actionType) => {
      builder.addCase(actionType, handleRejected)
    })

    builder.addCase(createBillStep.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { billStepId, stepId, temporaryId } = action.payload
      if (state.currentProcess) {
        const updatedBillSteps = [...state.currentProcess.bill_steps]
        state.currentProcess = {
          ...state.currentProcess,
          bill_steps: updatedBillSteps.map((billStep) => {
            if (billStep.id === temporaryId) {
              return {
                ...billStep,
                id: billStepId,
                step: stepId,
              }
            }
            return billStep
          }),
        }
      }
    })

    builder.addCase(createFormStep.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { formStepId, stepId, temporaryId } = action.payload
      if (state.currentProcess) {
        const updatedFormSteps = [...state.currentProcess.form_steps]
        state.currentProcess = {
          ...state.currentProcess,
          form_steps: updatedFormSteps.map((formStep) => {
            if (formStep.id === temporaryId) {
              return {
                ...formStep,
                id: formStepId,
                step: stepId,
              }
            }
            return formStep
          }),
        }
      }
    })

    builder.addCase(createInputStep.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { inputStepId, temporaryId, datetimeStepSub, identityStepSub } = action.payload
      const createdInputStep = state.currentProcess?.inputs.find(
        (inputStep) => inputStep.id === temporaryId,
      )
      if (createdInputStep && state.currentProcess) {
        const updatedInputSteps = [...state.currentProcess.inputs]
        state.currentProcess = {
          ...state.currentProcess,
          inputs: updatedInputSteps.map((inputStep) => {
            if (inputStep.id === temporaryId) {
              return {
                ...createdInputStep,
                id: inputStepId,
                sub: {
                  ...createdInputStep.sub,
                  datetime_step_sub: datetimeStepSub,
                  identity_step_sub: identityStepSub,
                } as InputStepSub0,
              }
            }
            return inputStep
          }),
        }
      }
    })

    builder.addCase(createSectionStep.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { sectionStepId, temporaryId } = action.payload
      const createdSectionStep = state.currentProcess?.subsections.find(
        (sectionStep) => sectionStep.id === temporaryId,
      )
      if (createdSectionStep && state.currentProcess) {
        const updatedSectionSteps = [...state.currentProcess.subsections]
        state.currentProcess = {
          ...state.currentProcess,
          subsections: updatedSectionSteps.map((sectionStep) => {
            if (sectionStep.id === temporaryId) {
              return {
                ...createdSectionStep,
                id: sectionStepId,
              }
            }
            return sectionStep
          }),
        }
      }
    })

    builder.addCase(createFormStyleElement.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { formStyleElementId, temporaryId } = action.payload
      const createdFormStyleElement = state.currentProcess?.page_breaks.find(
        (pageBreak) => pageBreak.id === temporaryId,
      )
      if (createdFormStyleElement && state.currentProcess) {
        const updatedPageBreaks = [...state.currentProcess.page_breaks]
        state.currentProcess = {
          ...state.currentProcess,
          page_breaks: updatedPageBreaks.map((pageBreak) => {
            if (pageBreak.id === temporaryId) {
              return {
                ...createdFormStyleElement,
                id: formStyleElementId,
              } as FormStyleElement
            }
            return pageBreak
          }),
        }
      }
    })

    builder.addCase(editBillStep.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { stepId, charges } = action.payload
      const updatedBillStep = state.currentProcess?.bill_steps.find(
        (billStep) => billStep.id === stepId,
      )
      if (updatedBillStep && state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          bill_steps: [...state.currentProcess.bill_steps].map((billStep) => {
            if (billStep.id === stepId) {
              return {
                ...billStep,
                charges: charges ? [...charges] : billStep.charges,
              }
            }
            return billStep
          }),
        }
      }
    })

    builder.addCase(copySectionStep.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { copiedSections, copiedInputs } = action.payload
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.map((inputStep) => {
            if (inputStep.id.endsWith('-copy')) {
              const newInputSubstep2 = copiedInputs[inputStep.id.split('-copy')[0]]
              return {
                ...inputStep,
                id: newInputSubstep2.id,
                section: inputStep.section
                  ? copiedSections[inputStep.section.split('-copy')[0]]
                  : null,
                sub: newInputSubstep2.sub,
              }
            }
            return inputStep
          }),
          subsections: state.currentProcess.subsections.map((sectionStep) => {
            if (sectionStep.id.endsWith('-copy')) {
              return {
                ...sectionStep,
                id: copiedSections[sectionStep.id.split('-copy')[0]],
                section: sectionStep.section
                  ? copiedSections[sectionStep.section.split('-copy')[0]]
                  : null,
              }
            }
            return sectionStep
          }),
        }
      }
    })
  },
})

export const {
  setCurrentProcess,
  setSelectedStep,
  setSelectedFormSubstep,
  setUserId,
  setExpressions,
  setModal,
  setStepNotifications,
  setSnackbarConfiguration,
  clearProcessEdit,
  createStepLocally,
  createInputStepLocally,
  createSectionStepLocally,
  createFormStyleElementLocally,
  copySectionStepLocally,
  addExpressionsLocally,
  deleteExpressionsLocally,
  deleteInputStepLocally,
  deleteFormStyleElementLocally,
  deleteSectionStepLocally,
  deleteStepAssignmentLocally,
  deleteStepLocally,
  updateBillStepLocally,
  updateDateTimeStepLocally,
  updateFormStepLocally,
  updateFormStyleElementLocally,
  updateIdentityStepLocally,
  updateInputStepLocally,
  updateSectionStepLocally,
  updateStepLocally,
} = processEditSlice.actions

export default processEditSlice.reducer
